import type Phaser from 'phaser';
import { THROW } from '../config';

/**
 * Returns the new x position when smoothly tracking from `current` toward
 * `target` with `factor` in [0,1]. Snaps when within 0.5 px to avoid jitter.
 */
export function lerpTowardTarget(current: number, target: number, factor: number): number {
  const f = Math.max(0, Math.min(1, factor));
  const next = current + (target - current) * f;
  return Math.abs(next - target) < 0.5 ? target : next;
}

export type HandFrame = 'hold' | 'wind-up' | 'release' | 'recoil';

/**
 * Pure state-advance for the throw animation. Given the elapsed ms within
 * the throw cycle, return the current frame and whether the "released"
 * boundary was just crossed (so the caller spawns a fruit exactly once).
 *
 * Boundaries: 0–25% hold, 25–50% wind-up, 50–75% release, 75–100% recoil.
 * "Released" fires when prevT < 0.50 and current t >= 0.50 — only valid
 * when `prevElapsedMs` was supplied.
 */
export function throwAdvance(
  elapsedMs: number,
  animMs: number,
  prevElapsedMs?: number,
): { frame: HandFrame; released: boolean } {
  const t = (elapsedMs % animMs) / animMs;
  let frame: HandFrame;
  if (t < 0.25) frame = 'hold';
  else if (t < 0.5) frame = 'wind-up';
  else if (t < 0.75) frame = 'release';
  else frame = 'recoil';
  const prevT = prevElapsedMs === undefined ? -1 : (prevElapsedMs % animMs) / animMs;
  const released = prevElapsedMs !== undefined && prevT < 0.5 && t >= 0.5;
  return { frame, released };
}

const FRAME_INDEX: Record<HandFrame, number> = {
  hold: 0,
  'wind-up': 1,
  release: 2,
  recoil: 3,
};

export interface HandDeps {
  scene: Phaser.Scene;
  /** Texture key for the hand sprite sheet (already loaded). */
  textureKey: string;
  /** Starting x in scene coords. */
  x: number;
  /** Y coordinate where the hand sits (typically near the top of the bowl). */
  y: number;
  /** Sprite width to render at in scene coords (preserves aspect from texture). */
  width: number;
  /** Called once the throw's release boundary is crossed. */
  onRelease: (x: number, y: number) => void;
}

export class Hand {
  readonly sprite: Phaser.GameObjects.Sprite;
  private targetX: number;
  private throwing = false;
  private throwElapsed = 0;
  private prevThrowElapsed = -1;
  private readonly handY: number;
  private readonly deps: HandDeps;

  constructor(deps: HandDeps) {
    this.deps = deps;
    this.sprite = deps.scene.add.sprite(deps.x, deps.y, deps.textureKey, 0);
    // Preserve the frame's natural aspect ratio. The hand drawing fills roughly
    // VISUAL_FRACTION of the frame's WIDTH, so scale the sprite up so the hand
    // visually matches the requested width.
    const VISUAL_FRACTION = 0.6;
    const frame = this.sprite.frame;
    const frameAspect = frame.height / frame.width;
    const spriteW = deps.width / VISUAL_FRACTION;
    this.sprite.setDisplaySize(spriteW, spriteW * frameAspect);
    this.targetX = deps.x;
    this.handY = deps.y;
  }

  setTargetX(x: number): void {
    this.targetX = x;
  }

  startThrow(): void {
    if (this.throwing) {
      this.throwElapsed = 0;
      this.prevThrowElapsed = -1;
      return;
    }
    this.throwing = true;
    this.throwElapsed = 0;
    this.prevThrowElapsed = -1;
  }

  tick(deltaMs: number): void {
    this.sprite.x = lerpTowardTarget(this.sprite.x, this.targetX, THROW.handLerpFactor);
    if (!this.throwing) {
      this.sprite.setFrame(FRAME_INDEX.hold);
      return;
    }
    this.throwElapsed += deltaMs;
    const prev = this.prevThrowElapsed < 0 ? undefined : this.prevThrowElapsed;
    const { frame, released } = throwAdvance(this.throwElapsed, THROW.handAnimMs, prev);
    this.sprite.setFrame(FRAME_INDEX[frame]);
    if (released) {
      this.deps.onRelease(this.sprite.x, this.handY);
    }
    this.prevThrowElapsed = this.throwElapsed;
    if (this.throwElapsed >= THROW.handAnimMs) {
      this.throwing = false;
      this.sprite.setFrame(FRAME_INDEX.hold);
    }
  }

  destroy(): void {
    this.sprite.destroy();
  }
}
